/* @flow */
import React, { useContext, memo } from 'react'
import S from './Contact9.styled'
import { validation } from './Contact9.consts'
import ActionGetter from './Actions'
import Mapper from '@website/common/components/Mapper'
import Show from '@website/common/components/Show/Show'
import { isValidEmail, isValidPhoneNumber } from '@website/common/utils'
import AddButton from '@website/common/components/AddButton'
import Icon from '@website/common/components/Icon'
import { DispatchContext, EditingContext } from '@contexts'
import EditableContent from '@website/common/components/EditableContent'
import { ControlsWithModalOpener } from '@website/common/components/Controlled'
import { translate } from '@editor/common/utils/translations'

const Contact9 = memo(props => {
  const {
    data: {
      title,
      titleAlignment,
      blocks,
      backgroundImgUrl,
      bgOverlayTransparency,
      backgroundImgDimensions
    }
  } = props
  const dispatcher = useContext(DispatchContext)
  const { isEditing } = useContext(EditingContext)
  const Actions = ActionGetter(dispatcher)
  const isEmailOrNumber = (
    text,
    idx,
    isEditing,
    key,
    alignmentKey,
    alignment
  ) => {
    if (isEditing || (!isValidEmail(text) && !isValidPhoneNumber(text))) {
      return (
        <EditableContent
          areControlsHidden
          as={S.BlockParagraph}
          text={text}
          alignment={alignment}
          required={blocks.length === 1}
          maxCount={validation.blockParagraphMaxChar}
          isEditing={isEditing}
          className="WM_GLOBAL_paragraph14"
          onChange={(newParagraph: string) => {
            Actions.changeBlockParagraph(newParagraph, idx, key)
          }}
          changeAlignment={alignment => {
            Actions.changeBlockParagraphAlignment(alignment, alignmentKey, idx)
          }}
        />
      )
    }
    const hrefPrefix = isValidEmail(text) ? 'mailto:' : 'tel:'
    return (
      <S.A
        alignment={alignment}
        href={`${hrefPrefix}${text}`}
        className="WM_GLOBAL_paragraph14"
      >
        {text}
      </S.A>
    )
  }
  return (
    <S.StyledContainer
      backgroundImgUrl={backgroundImgUrl}
      backgroundImgDimensions={backgroundImgDimensions}
      bgOverlayTransparency={bgOverlayTransparency}
      isEditing={isEditing}
    >
      <S.WMCustomContainer>
        <EditableContent
          as={S.Title}
          text={title}
          alignment={titleAlignment}
          maxCount={validation.titleMaxChar}
          className="WM_GLOBAL_heading42"
          onChange={Actions.changeTitle}
          changeAlignment={Actions.changeTitleAlignment}
        />
        <S.FeatureItemWrap areFourBlock={blocks.length === 4}>
          <Mapper
            data={blocks}
            render={(block: string, idx: number) => (
              <Show when={[block.title, block.paragraph1, block.paragraph2]}>
                <S.ItemWrap key={idx} isMoreThanFour={blocks.length > 4}>
                  <ControlsWithModalOpener
                    actions={Actions.blockActions({
                      idx,
                      showRemove: blocks.length > validation.blocks.min
                    })}
                    style={{ width: '100%' }}
                  >
                    <EditableContent
                      as={S.BlockTitle}
                      text={block.title}
                      alignment={block.blockTitleALignment}
                      required={blocks.length === 1}
                      maxCount={validation.blockTitleMaxChar}
                      className="WM_GLOBAL_heading20"
                      onChange={(newTitle: string) => {
                        Actions.changeBlockTitle(newTitle, idx)
                      }}
                      changeAlignment={(alignment: string) =>
                        Actions.changeBlockTitleAlignment(alignment, idx)
                      }
                    />
                    {isEmailOrNumber(
                      block.paragraph1,
                      idx,
                      isEditing,
                      'paragraph1',
                      'paragraph1Alignment',
                      block.paragraph1Alignment
                    )}
                    {isEmailOrNumber(
                      block.paragraph2,
                      idx,
                      isEditing,
                      'paragraph2',
                      'paragraph2Alignment',
                      block.paragraph2Alignment
                    )}
                  </ControlsWithModalOpener>
                </S.ItemWrap>
              </Show>
            )}
          />
        </S.FeatureItemWrap>
      </S.WMCustomContainer>
      <AddButton
        onAdd={Actions.addBlock}
        toShow={blocks.length < validation.blocks.max}
        style={{
          marginTop: '20px'
        }}
      >
        <Icon name="glyph-add" className="icon" size="normal" />
        {translate('add_block_label')}
      </AddButton>
    </S.StyledContainer>
  )
})

export default Contact9
